// Reads a scenepack file name (and, if the file came from a folder, its folder
// path) and works out what it is, so nobody has to fill in a form per file.
//
// The everyday naming this reads:
//
//   Character - Movie Quality @creator
//
//   Yelena Romanoff - Hawkeye 4K REMUX @alphascps
//   Brian Moser - Dexter Season 1 (Upscaled) 4K @alphascps
//
// (A season / part / edition after the movie becomes the pack's own name.) The longer
//   Movie (Year) - Character - Pack name [Quality].mkv
// and folders  Movie (Year)/Character/file  work too. Dashes need spaces around them
// ("Spider-Man" is fine); every part is optional; @handles are ignored.
//
// This is only a first reading: the server refines it (which movie exactly, its IMDb /
// Rotten Tomatoes links, year, poster). Returns { movie, year, character, title, quality,
// premium, credit, notes } where `quality` is one of the site's four values or "".

export const QUALITIES = ["1080P", "1080P REMUX", "4K", "4K REMUX"];

const VIDEO_OR_ARCHIVE = /\.(mkv|mp4|mov|m4v|avi|webm|ts|m2ts|zip|rar|7z)$/i;

/** True for files that could be a scenepack (skips .txt/.nfo/images/etc). */
export function looksLikeScenepack(fileName) {
  return VIDEO_OR_ARCHIVE.test(fileName || "");
}

function stripExtension(name) {
  return name.replace(/\.[a-z0-9]{2,4}$/i, "");
}

// Words that describe the encode, not the scene: dropped from the name.
const NOISE = /\b(x264|x265|h\.?264|h\.?265|hevc|avc|aac|ac3|eac3|dts(?:-hd)?|truehd|atmos|hdr10\+?|hdr|dv|dolby ?vision|10 ?bit|web-?dl|blu-?ray|bdrip|hdtv)\b/gi;

function readQuality(text, notes) {
  let out = text;
  const has = (re) => re.test(out);
  const is4k = has(/\b(2160p?|4k|uhd)\b/i);
  const is1080 = has(/\b1080[pi]?\b/i);
  const is720 = has(/\b(720p?|576p?|480p?)\b/i);
  const remux = has(/\bremux\b/i);

  out = out
    .replace(/\b(2160p?|4k|uhd|1080[pi]?|720p?|576p?|480p?|remux)\b/gi, " ")
    .replace(NOISE, " ");

  let quality = "";
  if (is4k) quality = remux ? "4K REMUX" : "4K";
  else if (is1080) quality = remux ? "1080P REMUX" : "1080P";
  else if (is720) notes.push("720p or lower isn't offered — pick the closest quality");
  else if (remux) notes.push("REMUX found but no resolution — pick the quality");
  return { text: out, quality };
}

// "(2025)" or "[2025]"
function readYear(text) {
  const m = text.match(/[([]\s*((?:19|20)\d{2})\s*[)\]]/);
  if (!m) return { text, year: 0 };
  return { text: text.replace(m[0], " "), year: parseInt(m[1], 10) };
}

// S01E03 -> "Season 1 Episode 3", S02 -> "Season 2" (scene-style names)
function expandSeasons(text) {
  return text
    .replace(/\bS(\d{1,2})E(\d{1,3})\b/gi, (_, s, e) => `Season ${parseInt(s, 10)} Episode ${parseInt(e, 10)}`)
    .replace(/\bS(\d{1,2})\b/g, (_, s) => `Season ${parseInt(s, 10)}`);
}

// "Dexter Season 1, Part A (Upscaled)" -> movie "Dexter", label "Season 1, Part A (Upscaled)"
const LABEL_START = /\bSeason\s+\d+|\bPart\s+(?:\d+|[A-Z]\b)|\bEpisode\s+\d+|\((?:upscaled|remake|remaster(?:ed)?|extended|uncut)\)/i;
function splitLabel(text) {
  const m = text.match(LABEL_START);
  if (!m) return { movie: text, label: "" };
  return { movie: text.slice(0, m.index).trim(), label: text.slice(m.index).trim() };
}

function tidy(text) {
  return text
    .replace(/[([]\s*[)\]]/g, " ") // empty brackets left behind
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-–—_.,|]+|[\s\-–—_.,|]+$/g, "")
    .trim();
}

// A folder name may carry a year too: "Daredevil (2025)"
function readFolder(name) {
  const { text, year } = readYear(name.replace(/_/g, " "));
  return { name: tidy(text), year };
}

export function parseScenepackName(fileName, relativePath = "") {
  const notes = [];
  let text = stripExtension(String(fileName || "")).replace(/_/g, " ");
  // scene-style "Some.Name.Here.mkv": treat dots as spaces when there are no spaces
  if (!/\s/.test(text) && (text.match(/\./g) || []).length >= 2) text = text.replace(/\./g, " ");

  // @creator handles are the uploader's credit, not part of the name
  const credit = (text.match(/@([\w.]+)/) || [])[1] || "";
  text = text.replace(/@[\w.]+/g, " ");

  // [AA+] flag
  let premium = false;
  if (/(^|[\s([])aa\+(?=$|[\s)\]])|[([]\s*premium\s*[)\]]/i.test(text)) {
    premium = true;
    text = text.replace(/[([]\s*aa\+\s*[)\]]|\baa\+(?=$|\s)|[([]\s*premium\s*[)\]]/gi, " ");
  }

  const q = readQuality(text, notes);
  text = q.text;
  const y = readYear(text);
  text = expandSeasons(y.text);
  let year = y.year;

  const parts = tidy(text)
    .split(/\s+[-–—]\s+|\s+\|\s+/)
    .map(tidy)
    .filter(Boolean);

  let movie = "";
  let character = "";
  let title = "";
  if (parts.length >= 3) {
    [movie, character] = parts;
    title = parts.slice(2).join(" - ");
  } else if (parts.length === 2) {
    // "Character - Movie [Season 1 ...]": what follows the dash is the movie
    character = parts[0];
    const split = splitLabel(parts[1]);
    movie = split.movie;
    title = split.label;
  } else if (parts.length === 1) {
    character = parts[0];
  }

  // Folders fill in whatever the file name didn't say: Movie/Character/file
  const folders = String(relativePath || "")
    .split("/")
    .filter(Boolean)
    .slice(0, -1) // drop the file itself
    .map(readFolder);
  if (folders.length >= 2) {
    const [f1, f2] = folders.slice(-2);
    if (!movie) { movie = f1.name; year = year || f1.year; }
    if (!character) { character = f2.name; } else if (!title && parts.length === 1) { title = character; character = f2.name; }
  } else if (folders.length === 1) {
    if (!movie) { movie = folders[0].name; year = year || folders[0].year; }
  }

  // "Movie - Pack" with only a folder for the movie is fine; a lone name with a
  // character folder means the name is the pack name (handled above).
  return { movie, year, character, title, quality: q.quality, premium, credit, notes };
}
